import { Injectable, Inject, Optional } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { EMPTY, Observable, combineLatest, concat, defer, interval, merge, of, throwError, timer } from 'rxjs';
import { distinctUntilChanged, filter, finalize, map, retry, switchMap } from 'rxjs/operators';
import { DashboardFilters } from '../dashboard-filters/dashboard-filters.service';
import { WEBSOCKET_FACTORY, webSocket, WebSocketSubject } from 'src/app/modules/web-socket';
import { ANALYTICS_WEBSOCKET_URL } from './analytics-websocket-url.injection-token';

@Injectable({ providedIn: 'root' })
export class DashboardWebSocketService {
  private static readonly pingIntervalMS = 4 * 60 * 1000; // keep-alive well below AWS API Gateway's idle timeout

  private readonly socketSubject$: Observable<WebSocketSubject<any>>;

  constructor(
    private readonly afAuth: AngularFireAuth,
    @Inject(WEBSOCKET_FACTORY) private readonly wsFactory: typeof webSocket,
    @Optional() @Inject(ANALYTICS_WEBSOCKET_URL) readonly wsUrl?: string,
  ) {
    const wsUrl$ = wsUrl ? of(wsUrl) : EMPTY;

    const authenticatedWsUrl$ = wsUrl$.pipe(
      switchMap(url =>
        this.afAuth.idToken.pipe(
          filter((token): token is string => !!token),
          map(token => `${url}?token=${token}`),
        ),
      ),
    );

    this.socketSubject$ = authenticatedWsUrl$.pipe(map(url => this.wsFactory(url)));
  }

  private subscribe(socket$: WebSocketSubject<any>, botId: string, filters: DashboardFilters): WebSocketSubject<any> {
    console.log('Subscribing to WebSocket with filters', { botId, filters });
    socket$.next({
      action: 'subscribe',
      botId,
      worklist: filters.worklist || undefined,
      startDate: filters.startDate.toISOString(),
      endDate: filters.endDate.toISOString(),
      intent: filters.intent || undefined,
      assignedTo: filters.assignedTo || undefined,
    });
    return socket$;
  }

  connect(botId: string, filters$: Observable<DashboardFilters>): Observable<void> {
    return defer(() =>
      combineLatest({
        socket: this.socketSubject$,
        filters: filters$,
      }).pipe(
        map(({ socket, filters }) => ({ socket, botId, filters })),
        switchMap(value => merge(of(value), interval(DashboardWebSocketService.pingIntervalMS).pipe(map(() => value)))),
        map(({ socket, botId, filters }) => this.subscribe(socket, botId, filters)),
        distinctUntilChanged(),
        switchMap(socket$ =>
          concat(
            socket$.pipe(finalize(() => socket$.complete())),
            throwError(() => new Error('WebSocket connection closed')),
          ),
        ),
        filter(msg => {
          console.debug('Received WebSocket message', msg);
          if (msg?.message === 'Forbidden') throw new Error('Forbidden');
          return msg?.action === 'conversationUpdated';
        }),
        retry({
          delay: (_, retryCount) => timer(Math.min(1000 * 2 ** (retryCount - 1), 30_000)),
        }),
      ),
    );
  }
}
